/* rk-sk.js — JS de la familia rk-sk (primera: /products/safety-kit). Calca el comportamiento MEDIDO
   con clicks reales en el original GemPages (ver 03-migracion/safety-kit/spec-radiografia.md):
   - rk-sk-gallery: imagen principal + tira de miniaturas, ambas CIRCULARES (loop). La miniatura activa
     queda siempre primera; las flechas de la tira mueven 1 miniatura sin cambiar la principal.
   - rk-sk-accordion: un ítem abierto a la vez, max-height animado, ícono plus/minus.
   - rk-sk-carousel: carrusel de 1 ítem con loop (barra de confianza mobile).
   Sin dependencias, sin localStorage (webviews de Meta). Guardas customElements.get porque el script
   puede incluirse desde varias secciones. El add-to-cart NO vive acá: lo hace rk-alk.js (rk-atc). */

(() => {
  const reducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mod = (value, n) => ((value % n) + n) % n;

  /* Riel circular: N ítems posicionados con la var CSS --pos (el CSS traduce --pos a translateX).
     El ítem "offset" queda en 0 y el resto se reparte en [-(N-1-R), R], como GemPages. Los ítems que dan
     la vuelta no cruzan la zona visible: si salen de la vista se animan un paso y luego se reubican; si
     entran a la vista se reubican fuera del borde y luego se animan. */
  class RkSkRail {
    constructor(items, visibleCount) {
      this.items = items;
      this.n = items.length;
      this.visibleCount = visibleCount;
      this.r = this.n - 1 - Math.floor((this.n - 1) / 2);
      this.offset = 0;
      this.pos = items.map((_, i) => this.#norm(i, 0));
      this.timers = [];
      this.pending = [];
      items.forEach((el, i) => el.style.setProperty('--pos', this.pos[i]));
    }

    #norm(i, offset) {
      const rel = mod(i - offset, this.n);
      return rel <= this.r ? rel : rel - this.n;
    }

    #touchesView(a, b) {
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      return hi > -1 && lo < this.visibleCount();
    }

    #durationMs(el) {
      return (parseFloat(getComputedStyle(el).transitionDuration) || 0) * 1000;
    }

    #flushPending() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
      if (!this.pending.length) return;
      this.pending.forEach(([el, p]) => {
        el.style.transition = 'none';
        el.style.setProperty('--pos', p);
      });
      void this.items[0].offsetWidth;
      this.pending.forEach(([el]) => { el.style.transition = ''; });
      this.pending = [];
    }

    moveBy(delta) {
      if (this.n < 2 || delta === 0) return;
      this.#flushPending();
      const newOffset = mod(this.offset + delta, this.n);
      const animate = !reducedMotion();
      const snapped = [];
      const entering = [];
      const leaving = [];
      let maxMs = 0;

      this.items.forEach((el, i) => {
        const from = this.pos[i];
        const to = this.#norm(i, newOffset);
        const expected = from - delta;
        this.pos[i] = to;
        if (animate && to === expected) {
          el.style.transition = '';
          el.style.setProperty('--pos', to);
          return;
        }
        if (animate && this.#touchesView(from, expected)) {
          /* sale de la vista: animar hasta el borde y reubicar al terminar */
          el.style.transition = '';
          el.style.setProperty('--pos', expected);
          leaving.push([el, to]);
          maxMs = Math.max(maxMs, this.#durationMs(el));
          return;
        }
        el.style.transition = 'none';
        if (animate && this.#touchesView(to + delta, to)) {
          /* entra a la vista: aparecer fuera del borde y animar hacia adentro */
          el.style.setProperty('--pos', to + delta);
          entering.push([el, to]);
        } else {
          el.style.setProperty('--pos', to);
          snapped.push(el);
        }
      });

      void this.items[0].offsetWidth;
      snapped.forEach((el) => { el.style.transition = ''; });
      entering.forEach(([el, p]) => {
        el.style.transition = '';
        el.style.setProperty('--pos', p);
      });
      if (leaving.length) {
        this.pending = leaving;
        this.timers.push(setTimeout(() => this.#flushPending(), maxMs + 30));
      }
      this.offset = newOffset;
    }

    shortestDelta(target) {
      let d = mod(target - this.offset, this.n);
      if (d > this.r) d -= this.n;
      return d;
    }
  }

  /* Swipe con pointer events (touch y mouse). Devuelve dx al soltar si superó el umbral. */
  const onSwipe = (el, handler) => {
    let startX = null;
    let startY = 0;
    let pointerId = null;
    el.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
    });
    el.addEventListener('pointerup', (event) => {
      if (startX === null || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        el.dataset.skDragged = '1';
        setTimeout(() => delete el.dataset.skDragged, 0);
        handler(dx);
      }
    });
    el.addEventListener('pointercancel', () => { startX = null; });
    el.addEventListener('dragstart', (event) => event.preventDefault());
  };

  if (!customElements.get('rk-sk-gallery')) {
    class RkSkGallery extends HTMLElement {
      connectedCallback() {
        if (this.dataset.skReady) return;
        this.dataset.skReady = '1';
        this.slides = [...this.querySelectorAll('[data-sk-slide]')];
        this.thumbs = [...this.querySelectorAll('[data-sk-thumb]')];
        this.mainViewport = this.querySelector('[data-sk-main]');
        this.thumbViewport = this.querySelector('[data-sk-thumbs]');
        if (this.slides.length < 2) return;
        this.active = 0;
        this.main = new RkSkRail(this.slides, () => 1);
        this.strip = new RkSkRail(this.thumbs, () => {
          const first = this.thumbs[0];
          if (!first || !this.thumbViewport) return 1;
          const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(this).getPropertyValue('--rk-skb-tgap') || '5');
          return Math.max(1, Math.ceil(this.thumbViewport.clientWidth / step));
        });

        this.querySelectorAll('[data-sk-go]').forEach((btn) => {
          btn.addEventListener('click', () => this.go(btn.dataset.skGo === 'next' ? 1 : -1));
        });
        this.querySelectorAll('[data-sk-tgo]').forEach((btn) => {
          btn.addEventListener('click', () => this.strip.moveBy(btn.dataset.skTgo === 'next' ? 1 : -1));
        });
        this.thumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', () => {
            if (this.thumbViewport && this.thumbViewport.dataset.skDragged) return;
            this.select(i);
          });
        });
        if (this.mainViewport) onSwipe(this.mainViewport, (dx) => this.go(dx < 0 ? 1 : -1));
        if (this.thumbViewport) {
          onSwipe(this.thumbViewport, (dx) => {
            const step = this.thumbs[0].getBoundingClientRect().width + 5;
            const steps = Math.max(1, Math.round(Math.abs(dx) / step));
            this.strip.moveBy(dx < 0 ? steps : -steps);
          });
        }
        const eager = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
        if (document.readyState === 'complete') eager();
        else window.addEventListener('load', eager, { once: true });
        this.#mark();
      }

      go(delta) {
        const target = mod(this.active + delta, this.slides.length);
        this.main.moveBy(delta);
        this.strip.moveBy(this.strip.shortestDelta(target));
        this.active = target;
        this.#mark();
      }

      select(index) {
        if (index === this.active) {
          this.strip.moveBy(this.strip.shortestDelta(index));
          return;
        }
        const mainDelta = this.main.shortestDelta(index);
        const stripDelta = this.strip.pos[index] >= 0 ? this.strip.pos[index] : this.strip.shortestDelta(index);
        this.main.moveBy(mainDelta);
        this.strip.moveBy(stripDelta);
        this.active = index;
        this.#mark();
      }

      #mark() {
        this.thumbs.forEach((thumb, i) => {
          const on = i === this.active;
          thumb.classList.toggle('is-active', on);
          if (on) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', i === this.active ? 'false' : 'true'));
      }
    }
    customElements.define('rk-sk-gallery', RkSkGallery);
  }

  if (!customElements.get('rk-sk-accordion')) {
    class RkSkAccordion extends HTMLElement {
      connectedCallback() {
        if (this.dataset.skReady) return;
        this.dataset.skReady = '1';
        this.items = [...this.querySelectorAll('[data-sk-acc-item]')];
        this.items.forEach((item) => {
          const head = item.querySelector('[data-sk-acc-head]');
          const body = item.querySelector('[data-sk-acc-body]');
          if (!head || !body) return;
          body.addEventListener('transitionend', (event) => {
            if (event.propertyName === 'max-height' && item.classList.contains('is-open')) body.style.maxHeight = 'none';
          });
          head.addEventListener('click', () => this.toggle(item));
        });
      }

      toggle(item) {
        const isOpen = item.classList.contains('is-open');
        this.items.forEach((other) => {
          if (other !== item && other.classList.contains('is-open')) this.#close(other);
        });
        if (isOpen) this.#close(item);
        else this.#open(item);
      }

      #open(item) {
        const head = item.querySelector('[data-sk-acc-head]');
        const body = item.querySelector('[data-sk-acc-body]');
        item.classList.add('is-open');
        head.setAttribute('aria-expanded', 'true');
        body.style.maxHeight = `${body.scrollHeight}px`;
        if (reducedMotion()) body.style.maxHeight = 'none';
      }

      #close(item) {
        const head = item.querySelector('[data-sk-acc-head]');
        const body = item.querySelector('[data-sk-acc-body]');
        body.style.maxHeight = `${body.scrollHeight}px`;
        void body.offsetHeight;
        body.style.maxHeight = '0px';
        item.classList.remove('is-open');
        head.setAttribute('aria-expanded', 'false');
      }
    }
    customElements.define('rk-sk-accordion', RkSkAccordion);
  }

  if (!customElements.get('rk-sk-carousel')) {
    class RkSkCarousel extends HTMLElement {
      connectedCallback() {
        if (this.dataset.skReady) return;
        this.dataset.skReady = '1';
        this.slides = [...this.querySelectorAll('[data-sk-cslide]')];
        if (this.slides.length < 2) return;
        this.rail = new RkSkRail(this.slides, () => 1);
        this.querySelectorAll('[data-sk-cgo]').forEach((btn) => {
          btn.addEventListener('click', () => this.rail.moveBy(btn.dataset.skCgo === 'next' ? 1 : -1));
        });
        const viewport = this.querySelector('[data-sk-cviewport]');
        if (viewport) onSwipe(viewport, (dx) => this.rail.moveBy(dx < 0 ? 1 : -1));
      }
    }
    customElements.define('rk-sk-carousel', RkSkCarousel);
  }
})();
